import { useTouchDevice } from '@terminal/hooks/MediaQueryHook';
import { mobileAlfredPaths } from '@terminal/Root';
import { appPaths } from '@terminal/routes/app-paths';
import { queryKeys } from '@terminal/routes/query-params';
import { POSTHOG_TRACKING_SOURCES } from '@terminal/services/tracking/posthog/constants';
import { usePosthogEvents } from '@terminal/services/tracking/posthog/use-posthog-events/usePosthogEvents';
import { useAuthStore } from '@terminal/stores/use-auth/store/authStore';
import { useFlags } from '@terminal/stores/use-flags/useFlags';
import { useUser } from '@terminal/stores/use-user/useUser';
import { getSafePostLoginRedirect } from '@terminal/utils/get-safe-post-login-redirect';
import { useReasonerParams } from '@terminal/views/alfred/research/hooks/useReasonerParams';
import { useChatNavigation } from '@terminal/views/alfred/use-chat-navigation/useChatNavigation';
import { ReactNode, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router';

interface AppHistoryProps {
  children: ReactNode;
}

export const AppHistory = (props: AppHistoryProps) => {
  const { user } = useUser(({ user }) => ({ user }));
  const {
    isAuthenticated,
    authProcessing,
    isInitError,
    externalLoginRedirect,
  } = useAuthStore(state => ({
    isAuthenticated: !!state.token,
    authProcessing: state.authProcessing,
    isInitError: state.isInitError,
    externalLoginRedirect: state.externalLoginRedirect,
  }));
  const externalRedirectSent = useRef<string | undefined>(undefined);
  const location = useLocation();
  const navigate = useNavigate();
  const { trackDashboardOpen } = usePosthogEvents();
  const isTouchDevice = useTouchDevice();
  const isWhatsAppFeatureEnabled = useFlags(state =>
    state.getFeatureFlagState('alfred-whatsapp-integration')
  );
  const { conversationId } = useReasonerParams();
  const { navigateToConversation, setAlfredTrackingSource } =
    useChatNavigation();

  // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: Keep external returns and the existing mobile fallback with one navigation owner.
  useEffect(() => {
    if (!user || !isAuthenticated) {
      externalRedirectSent.current = undefined;
      return;
    }
    if (authProcessing || isInitError) {
      return;
    }
    const queryParams = new URLSearchParams(location.search);
    const safeRedirect = getSafePostLoginRedirect(
      externalLoginRedirect ?? queryParams.get(queryKeys.redirect)
    );

    if (
      safeRedirect?.kind === 'external' &&
      externalRedirectSent.current !== safeRedirect.href
    ) {
      externalRedirectSent.current = safeRedirect.href;
      window.location.href = safeRedirect.href;
      return;
    }

    if (safeRedirect?.kind !== 'relative') {
      return;
    }
    const targetPathname = new URL(safeRedirect.href, window.location.origin)
      .pathname;
    if (
      isTouchDevice &&
      (!isWhatsAppFeatureEnabled || !mobileAlfredPaths.includes(targetPathname))
    ) {
      navigateToConversation(conversationId, {
        trackingSource: POSTHOG_TRACKING_SOURCES.LOGIN,
      });
      return;
    }
    if (targetPathname === appPaths.alfred) {
      setAlfredTrackingSource(POSTHOG_TRACKING_SOURCES.LOGIN);
    }
    if (safeRedirect.href === appPaths.base) {
      trackDashboardOpen('login');
    }
    navigate(safeRedirect.href, { replace: true });
  }, [
    location,
    user,
    navigate,
    trackDashboardOpen,
    isAuthenticated,
    authProcessing,
    isInitError,
    externalLoginRedirect,
    isTouchDevice,
    isWhatsAppFeatureEnabled,
    conversationId,
    navigateToConversation,
    setAlfredTrackingSource,
  ]);

  return <>{props.children}</>;
};
